<admin-app-header></admin-app-header>

<div class="financial-health-page">
    <div class="container-fluid">
        <!-- Page Header -->
        <div class="page-header">
            <div class="page-header__title">
                <h1>Financial Health</h1>
                <p class="page-header__subtitle">Aging analysis and portfolio health metrics</p>
            </div>
            <div class="page-header__actions">
                <button (click)="loadFinancialHealthData()" class="btn btn-secondary" [disabled]="isLoading"><i class="fa-solid fa-refresh" [class.fa-spin]="isLoading"></i> Refresh</button>
            </div>
        </div>

        <!-- Loading State -->
        <div class="loading-state" *ngIf="isLoading">
            <div class="spinner"></div>
            <p>Loading financial health data...</p>
        </div>

        <div *ngIf="!isLoading">
            <!-- Summary Cards Row -->
            <div class="summary-cards">
                <div class="summary-card summary-card--danger">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-file-invoice-dollar"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">R{{ formatCurrency(totalOverdueParkerAmount) }}</span>
                        <span class="summary-card__label">Overdue Parker Invoices</span>
                        <span class="summary-card__subtext">{{ totalOverdueParkerInvoices }} invoices</span>
                    </div>
                </div>
                <div class="summary-card summary-card--warning">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-hand-holding-dollar"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">R{{ formatCurrency(totalUnpaidMerchantAmount) }}</span>
                        <span class="summary-card__label">Unpaid Merchant Payouts</span>
                        <span class="summary-card__subtext">{{ totalUnpaidMerchantPayouts }} invoices</span>
                    </div>
                </div>
                <div class="summary-card summary-card--info">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-building"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ merchantPayoutDetails.length }}</span>
                        <span class="summary-card__label">Portfolios with Outstanding</span>
                        <span class="summary-card__subtext">Merchants awaiting payout</span>
                    </div>
                </div>
            </div>

            <!-- Two Column Layout -->
            <div class="aging-grid">
                <!-- Parker Invoices Aging -->
                <div class="aging-section">
                    <div class="section-header">
                        <h2><i class="fa-solid fa-user-clock"></i> Parker Invoices Aging</h2>
                        <p>Invoices owed to ParkUpp (not yet paid by parkers)</p>
                    </div>

                    <div class="aging-table">
                        <div class="aging-table__header">
                            <span class="aging-table__col aging-table__col--period">Period</span>
                            <span class="aging-table__col aging-table__col--count">Count</span>
                            <span class="aging-table__col aging-table__col--amount">Amount</span>
                            <span class="aging-table__col aging-table__col--action"></span>
                        </div>

                        <div class="aging-bucket" *ngFor="let bucket of parkerInvoicesAging; let i = index" [ngClass]="{ 'aging-bucket--danger': i >= 2, 'aging-bucket--warning': i === 1 }">
                            <div class="aging-bucket__row" (click)="toggleBucket('parker', i)">
                                <span class="aging-bucket__col aging-bucket__col--period">
                                    <i class="fa-solid" [ngClass]="isBucketExpanded('parker', i) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                    {{ bucket.label }}
                                </span>
                                <span class="aging-bucket__col aging-bucket__col--count">{{ bucket.count }}</span>
                                <span class="aging-bucket__col aging-bucket__col--amount">R{{ formatCurrency(bucket.amount) }}</span>
                                <span class="aging-bucket__col aging-bucket__col--action">
                                    <span class="aging-bucket__percentage" *ngIf="totalOverdueParkerAmount > 0"> {{ ((bucket.amount / totalOverdueParkerAmount) * 100).toFixed(0) }}% </span>
                                </span>
                            </div>

                            <!-- Expanded Details -->
                            <div class="aging-bucket__details" *ngIf="isBucketExpanded('parker', i) && bucket.invoices.length > 0">
                                <table class="detail-table">
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            <th>Parking</th>
                                            <th>Amount</th>
                                            <th>Days Overdue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let invoice of bucket.invoices | slice: 0:10">
                                            <td>{{ invoice.clientName }}</td>
                                            <td>{{ invoice.parkingTitle | slice: 0:25 }}{{ invoice.parkingTitle.length > 25 ? '...' : '' }}</td>
                                            <td>R{{ invoice.amount | number: '1.0-0' }}</td>
                                            <td>
                                                <span class="days-badge" [ngClass]="{ 'days-badge--danger': invoice.daysOverdue > 60, 'days-badge--warning': invoice.daysOverdue > 30 }">
                                                    {{ invoice.daysOverdue }}d
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div class="detail-table__footer" *ngIf="bucket.invoices.length > 10">
                                    <a [routerLink]="['/admin/invoices']" [queryParams]="{ status: 'due' }">View all {{ bucket.invoices.length }} invoices →</a>
                                </div>
                            </div>
                        </div>

                        <div class="aging-table__total">
                            <span class="aging-table__col aging-table__col--period"><strong>Total Overdue</strong></span>
                            <span class="aging-table__col aging-table__col--count"
                                ><strong>{{ totalOverdueParkerInvoices }}</strong></span
                            >
                            <span class="aging-table__col aging-table__col--amount"
                                ><strong>R{{ formatCurrency(totalOverdueParkerAmount) }}</strong></span
                            >
                            <span class="aging-table__col aging-table__col--action"></span>
                        </div>
                    </div>
                </div>

                <!-- Merchant Payouts Aging -->
                <div class="aging-section">
                    <div class="section-header">
                        <h2><i class="fa-solid fa-building-user"></i> Merchant Payouts Aging</h2>
                        <p>Paid invoices not yet paid out to merchants</p>
                    </div>

                    <div class="aging-table">
                        <div class="aging-table__header">
                            <span class="aging-table__col aging-table__col--period">Period</span>
                            <span class="aging-table__col aging-table__col--count">Count</span>
                            <span class="aging-table__col aging-table__col--amount">Amount</span>
                            <span class="aging-table__col aging-table__col--action"></span>
                        </div>

                        <div class="aging-bucket" *ngFor="let bucket of merchantPayoutsAging; let i = index" [ngClass]="{ 'aging-bucket--danger': i >= 2, 'aging-bucket--warning': i === 1 }">
                            <div class="aging-bucket__row" (click)="toggleBucket('merchant', i)">
                                <span class="aging-bucket__col aging-bucket__col--period">
                                    <i class="fa-solid" [ngClass]="isBucketExpanded('merchant', i) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                    {{ bucket.label }}
                                </span>
                                <span class="aging-bucket__col aging-bucket__col--count">{{ bucket.count }}</span>
                                <span class="aging-bucket__col aging-bucket__col--amount">R{{ formatCurrency(bucket.amount) }}</span>
                                <span class="aging-bucket__col aging-bucket__col--action">
                                    <span class="aging-bucket__percentage" *ngIf="totalUnpaidMerchantAmount > 0"> {{ ((bucket.amount / totalUnpaidMerchantAmount) * 100).toFixed(0) }}% </span>
                                </span>
                            </div>

                            <!-- Expanded Details -->
                            <div class="aging-bucket__details" *ngIf="isBucketExpanded('merchant', i) && bucket.invoices.length > 0">
                                <table class="detail-table">
                                    <thead>
                                        <tr>
                                            <th>Merchant</th>
                                            <th>Parking</th>
                                            <th>Payout Amount</th>
                                            <th>Days Since Paid</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let invoice of bucket.invoices | slice: 0:10">
                                            <td>{{ invoice.merchantName | slice: 0:20 }}{{ invoice.merchantName.length > 20 ? '...' : '' }}</td>
                                            <td>{{ invoice.parkingTitle | slice: 0:25 }}{{ invoice.parkingTitle.length > 25 ? '...' : '' }}</td>
                                            <td>R{{ invoice.amount | number: '1.0-0' }}</td>
                                            <td>
                                                <span class="days-badge" [ngClass]="{ 'days-badge--danger': invoice.daysOverdue > 60, 'days-badge--warning': invoice.daysOverdue > 30 }">
                                                    {{ invoice.daysOverdue }}d
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div class="detail-table__footer" *ngIf="bucket.invoices.length > 10">
                                    <a [routerLink]="['/admin/payouts']">View all {{ bucket.invoices.length }} payouts →</a>
                                </div>
                            </div>
                        </div>

                        <div class="aging-table__total">
                            <span class="aging-table__col aging-table__col--period"><strong>Total Unpaid</strong></span>
                            <span class="aging-table__col aging-table__col--count"
                                ><strong>{{ totalUnpaidMerchantPayouts }}</strong></span
                            >
                            <span class="aging-table__col aging-table__col--amount"
                                ><strong>R{{ formatCurrency(totalUnpaidMerchantAmount) }}</strong></span
                            >
                            <span class="aging-table__col aging-table__col--action"></span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Merchant Payout Details -->
            <div class="merchant-section" *ngIf="merchantPayoutDetails.length > 0">
                <div class="section-header">
                    <h2><i class="fa-solid fa-building"></i> Unpaid Payouts by Portfolio</h2>
                    <p>Breakdown of unpaid amounts per merchant portfolio</p>
                </div>

                <div class="merchant-table-card">
                    <table class="merchant-table">
                        <thead>
                            <tr>
                                <th>Portfolio</th>
                                <th>Unpaid Invoices</th>
                                <th>Total Unpaid</th>
                                <th>Oldest Invoice</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let merchant of merchantPayoutDetails | slice: 0:15">
                                <td>
                                    <strong>{{ merchant.merchantName }}</strong>
                                </td>
                                <td>{{ merchant.unpaidInvoices }}</td>
                                <td class="amount-cell">R{{ formatCurrency(merchant.unpaidAmount) }}</td>
                                <td>
                                    <span *ngIf="merchant.oldestUnpaidDate" class="date-badge">
                                        {{ merchant.oldestUnpaidDate | date: 'dd MMM yyyy' }}
                                    </span>
                                </td>
                                <td>
                                    <a [routerLink]="['/admin/payouts']" class="btn btn-sm btn-outline-primary">
                                        <i class="fa-solid fa-eye"></i>
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="merchant-table-card__footer" *ngIf="merchantPayoutDetails.length > 15">
                        <span>Showing top 15 of {{ merchantPayoutDetails.length }} portfolios</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
